import Breadcrumbs from '@/components/Breadcrumbs';

import { sidebarCollapsedAtom } from '@/atoms/sidebar.atom';
import PortalTooltip from '@/components/PortalTooltip';
import { cn } from '@/lib/utils';
import { Sidebar } from '@mynaui/icons-react';
import { useAtom } from 'jotai';
import { Fragment, FunctionComponent, MouseEvent, PropsWithChildren, useId } from 'react';

interface iActionBase {
  tooltip?: string;
  hidden?: boolean;
}

interface iActionIcon extends iActionBase {
  icon: React.ReactNode;
  element?: never;
  onClick?: (e?: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  disabled?: boolean;
  destructive?: boolean;
  appearance?: 'primary' | 'secondary';
}

interface iActionElement extends iActionBase {
  element: React.ReactNode;
  icon?: never;
}

export type iAction = iActionIcon | iActionElement;

interface iPageSkeletonProps extends PropsWithChildren {
  steps?: { label: string; href: string; options?: { label: string; href: string; onPrefetch?: () => void }[] }[];
  hideSidebar?: boolean;
  actions?: (iAction | null | undefined | false)[];
  branding?: React.ReactNode;
}

const PageSkeleton: FunctionComponent<iPageSkeletonProps> = ({ children, steps, actions, hideSidebar, branding }) => {
  const id = useId();
  const [isSidebarOpen, setIsSidebarOpen] = useAtom(sidebarCollapsedAtom);

  const actionList = actions;
  return (
    <>
      <div className='flex-1 flex flex-col'>
        <div className='flex px-4 py-4 gap-2 h-15 items-center border-b border-slate-200/70 top-0 sticky z-10 bg-white dark:bg-stone-900 dark:border-stone-800 whitespace-nowrap'>
          {!hideSidebar && (
            <>
              <button
                className='size-7 flex items-center justify-center hover:bg-slate-100 active:bg-slate-200/70 dark:hover:bg-stone-800/50 dark:active:bg-stone-800/50 dark:text-white rounded-md'
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                <Sidebar className='size-4 stroke-2 text-slate-500 dark:text-stone-100' />
              </button>
              <div className='h-4 border-r dark:border-stone-600'></div>
            </>
          )}
          {branding && <div className='flex items-center justify-center pr-2'>{branding}</div>}
          <Breadcrumbs steps={steps || []} />
          <div className='flex-1'></div>
          {actionList?.map((action, index) => {
            if (!action)
              return (
                <div
                  key={`${id}-action-${index}`}
                  className='h-5 border-l border-slate-200 dark:border-stone-700 mx-0.5'
                />
              );
            if (action.hidden) return null;
            if ('element' in action && action.element) {
              return (
                <Fragment key={`${id}-action-${index}`}>
                  <div data-tooltip-id={`${id}-action-${index}`}>{action.element}</div>
                  <PortalTooltip id={`${id}-action-${index}`} delayShow={160}>
                    {action.tooltip}
                  </PortalTooltip>
                </Fragment>
              );
            }
            const { icon, onClick, disabled, destructive, appearance } = action as iActionIcon;
            return (
              <Fragment key={`${id}-action-${index}`}>
                <button
                  onClick={onClick}
                  disabled={disabled}
                  data-tooltip-id={`${id}-action-${index}`}
                  className={cn(
                    'size-7 flex items-center justify-center hover:bg-slate-100 active:bg-slate-200/70 rounded-md text-slate-500 hover:text-slate-700 [&>svg]:size-4 [&>svg]:stroke-[2.25px] dark:text-stone-100 dark:hover:text-stone-100 dark:hover:bg-stone-800/50 dark:active:bg-stone-800/50',
                    destructive && 'text-red-500 hover:text-red-600 hover:bg-red-50 active:bg-red-200/70',
                    'disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:text-slate-300 disabled:hover:bg-transparent',
                    appearance === 'primary' &&
                      'bg-slate-800 text-white hover:bg-slate-800/90 hover:text-white active:bg-slate-800/70 dark:bg-stone-700/70 dark:hover:bg-stone-700/90 dark:active:bg-stone-700',
                  )}>
                  {icon}
                </button>
                <PortalTooltip id={`${id}-action-${index}`} delayShow={160}>
                  {action.tooltip}
                </PortalTooltip>
              </Fragment>
            );
          })}
        </div>
        <div className='flex-1 flex flex-col overflow-hidden bg-slate-50/60 dark:bg-stone-900/60 relative'>
          {children}
        </div>
      </div>
    </>
  );
};

export default PageSkeleton;
